/**
 * Formats a raw phone number string into a more readable format.
 * Supports Uzbekistan (+998) and Russia (+7) phone numbers.
 * @param phoneNumber The raw phone number string.
 * @returns The formatted phone number string.
 */
export function formatPhoneNumber(phoneNumber: string): string {
  const cleaned = phoneNumber.replace(/\D/g, '') // Remove non-digits

  // Uzbekistan: 12 digits - 998XXYYYZZWW
  const uzMatch = cleaned.match(/^998(\d{2})(\d{3})(\d{2})(\d{2})$/)
  if (uzMatch) {
    return `+998 ${uzMatch[1]} ${uzMatch[2]} ${uzMatch[3]} ${uzMatch[4]}`
  }

  // Russia: 11 digits - 7XXXYYYZZZZ
  const ruMatch = cleaned.match(/^7(\d{3})(\d{3})(\d{2})(\d{2})$/)
  if (ruMatch) {
    return `+7 ${ruMatch[1]} ${ruMatch[2]} ${ruMatch[3]} ${ruMatch[4]}`
  }

  // Return original if it doesn't match expected pattern
  return phoneNumber
}

/**
 * Formats number with thousand separators.
 * @param price The price number.
 * @returns The formatted price string.
 */
export function formatNumber(price: number): string {
  return price.toLocaleString('ru-RU')
}

/**
 * Formats money with thousands separators and 1 decimal as per UI.
 * Example: 7523.0 → "7,523.0" (ru-RU → "7 523,0")
 */
export function formatMoney(amount: number, digits = 2): string {
  const safe = Number.isFinite(amount) ? amount : 0
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(safe)
}

/**
 * Formats ISO date string to dd.mm.yyyy format
 * @param dateString ISO date string like "2023-01-26T04:36:56.649399+03:00"
 * @returns Formatted date string like "26.01.2023"
 */
export function formatDateToDDMMYYYY(dateString: string): string {
  if (!dateString) 
    return ''

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) 
      return dateString

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
  }
  catch (error) {
    console.warn('Error formatting date:', error)
    return dateString
  }
}

/**
 * Formats date for display in DD.MM.YYYY format
 * Accepts both string (ISO date) and Date objects
 * @param dateInput Date string or Date object
 * @returns Formatted date string like "26.01.2023"
 */
export function formatDateDisplay(dateInput: string | Date): string {
  if (!dateInput) 
    return ''

  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return typeof dateInput === 'string' ? dateInput : ''
    }

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
  }
  catch (error) {
    console.warn('Error formatting date:', error)
    return typeof dateInput === 'string' ? dateInput : ''
  }
}

/**
 * Formats ISO datetime string to HH:MM format
 * @param dateTimeString ISO datetime string like "2023-01-26T12:44:44.320156+03:00"
 * @returns Formatted time string like "12:44"
 */
export function formatTimeToHHMM(dateTimeString: string): string {
  if (!dateTimeString) 
    return ''

  try {
    const date = new Date(dateTimeString)

    // Check if date is valid
    if (isNaN(date.getTime())) 
      return dateTimeString

    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${hours}:${minutes}`
  }
  catch (error) {
    console.warn('Error formatting time:', error)
    return dateTimeString
  }
}
